import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useAppState } from "../AppState";
import { api } from "../lib/api";
import {
  AddrLink,
  BlockLink,
  ErrorMsg,
  LiveBadge,
  Loading,
  PageHeader,
  Segmented,
  SlotPill,
} from "../components/Bits";
import { formatRelative, slotTimestampMs } from "../lib/format";
import { useChainParams } from "../hooks/useChainParams";
import { Paginator, usePaged } from "../components/Paginator";
import type { StoredDenunciationEntry } from "../lib/types";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "block_header", label: "Double block", short: "Block" },
  { key: "endorsement", label: "Double endorsement", short: "Endorsement" },
] as const;

const KIND: Record<StoredDenunciationEntry["kind"], { label: string; cls: string }> = {
  block_header: { label: "double block", cls: "text-bad bg-bad/10" },
  endorsement: { label: "double endorsement", cls: "text-warn bg-warn/10" },
  address: { label: "address", cls: "text-muted bg-panel2" },
  unknown: { label: "unknown", cls: "text-muted bg-panel2" },
};

function KindPill({ kind }: { kind: StoredDenunciationEntry["kind"] }) {
  const k = KIND[kind] ?? KIND.unknown;
  return (
    <span className={`inline-block whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-medium ${k.cls}`}>
      {k.label}
    </span>
  );
}

function HashLink({ hash }: { hash: string }) {
  return (
    <Link to={`/denunciation/${hash}`} className="font-mono whitespace-nowrap" title={hash}>
      {hash.slice(0, 8)}…{hash.slice(-4)}
    </Link>
  );
}

/** Newest-first denunciations. Links into the per-hash detail. */
export function Denunciations() {
  const { client, network } = useAppState();
  const params = useChainParams();
  const paged = usePaged(50);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]["key"]>("all");

  const q = useQuery({
    queryKey: [
      "recent-denunciations",
      network,
      client.endpoints(),
      paged.cursor,
      paged.limit,
    ],
    queryFn: () => api.recentDenunciations(client, paged.limit, paged.cursor),
    refetchInterval: paged.page === 0 ? 30_000 : false,
  });
  useEffect(() => {
    paged.setLastResponse(q.data ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data]);

  const all = q.data?.data ?? [];
  // ponytail: /v1/denunciations has no kind filter, so this filters the loaded page only.
  const rows = all
    .filter((d) => filter === "all" || d.kind === filter)
    .map((d) => {
      const ts = d.slot ? slotTimestampMs(d.slot, params) : null;
      return { d, ago: formatRelative(ts ?? d.first_seen_ts_ms) };
    });

  return (
    <>
      <Helmet>
        <title>{`Denunciations — ${network}`}</title>
      </Helmet>

      <PageHeader
        title="Denunciations"
        subtitle="Proofs that a staker signed two conflicting blocks or endorsements for the same slot."
      >
        {paged.page === 0 && <LiveBadge seconds={30} />}
      </PageHeader>

      <section className="card p-0 sm:p-0 overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 px-4 sm:px-6 py-4 sm:py-5">
          <h2 className="text-base sm:text-lg font-semibold mr-auto">Latest denunciations</h2>
          <Segmented label="Filter by kind" options={FILTERS} value={filter} onChange={setFilter} />
        </div>

        {q.isLoading ? (
          <div className="px-4 sm:px-6 pb-5"><Loading /></div>
        ) : q.isError ? (
          <div className="px-4 sm:px-6 pb-5"><ErrorMsg err={q.error} /></div>
        ) : rows.length === 0 ? (
          <div className="px-4 sm:px-6 pb-5 text-muted text-sm">
            {all.length === 0 ? "No denunciations yet." : "No denunciation on this page matches this filter."}
          </div>
        ) : (
          <>
            {/* Mobile: one card per denunciation. */}
            <ul className="md:hidden flex flex-col gap-2 px-4 pb-4">
              {rows.map(({ d, ago }) => (
                <li key={d.hash} className="flex flex-col gap-2.5 p-3.5 rounded-xl bg-bg border border-border">
                  <div className="flex items-center gap-2">
                    <KindPill kind={d.kind} />
                    <span className="ml-auto text-xs text-muted">{ago}</span>
                  </div>
                  <HashLink hash={d.hash} />
                  <div className="flex items-center gap-2 text-xs text-muted">
                    <SlotPill slot={d.slot} />
                    <span className="ml-auto">
                      {d.denounced_addr ? <AddrLink addr={d.denounced_addr} /> : "staker —"}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm min-w-[860px]">
                <thead className="text-muted text-xs bg-thead">
                  <tr>
                    <th className="text-left py-2.5 pl-4 sm:pl-6 pr-3">Denunciation</th>
                    <th className="text-left py-2.5 px-3">Kind</th>
                    <th className="text-left py-2.5 px-3">Denounced staker</th>
                    <th className="text-left py-2.5 px-3" title="Slot of the conflicting blocks or endorsements.">
                      Offence slot
                    </th>
                    <th className="text-left py-2.5 px-3">Included in</th>
                    <th className="text-right py-2.5 pr-4 sm:pr-6 pl-3 whitespace-nowrap">Slot time</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ d, ago }) => (
                    <tr key={d.hash} className="border-t border-border">
                      <td className="py-3 pl-4 sm:pl-6 pr-3">
                        <HashLink hash={d.hash} />
                      </td>
                      <td className="py-3 px-3">
                        <KindPill kind={d.kind} />
                      </td>
                      <td className="py-3 px-3">
                        {d.denounced_addr ? <AddrLink addr={d.denounced_addr} /> : <span className="text-muted/50">—</span>}
                      </td>
                      <td className="py-3 px-3">
                        <SlotPill slot={d.slot} />
                      </td>
                      <td className="py-3 px-3">
                        {d.included_block_id ? <BlockLink id={d.included_block_id} /> : <span className="text-muted/50">—</span>}
                      </td>
                      <td className="py-3 pr-4 sm:pr-6 pl-3 text-right text-muted text-[13px] whitespace-nowrap">
                        {ago}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        <div className="px-4 sm:px-6 pb-4 border-t border-border">
          <Paginator
            page={paged.page}
            pageSize={paged.pageSize}
            hasMore={paged.hasMore}
            loading={q.isFetching}
            count={all.length}
            onPrev={paged.prev}
            onNext={paged.next}
            prevLabel="← Newer"
            nextLabel="Older →"
          />
        </div>
      </section>
    </>
  );
}
